// src/components/songEdit/versions/VersionFiles.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import apiClient from '../../../utils/apiClient';
import { toast } from 'react-toastify';
import { useConfirmModal } from '../../common/ConfirmModal';
import { FILE_SERVER_URL } from '../../../config/api';
import './VersionFiles.css';

export interface VersionFile {
  file_id: number;
  version_id: number;
  file_type: 'bulletin' | 'chart' | 'choir' | 'instrumental' | 'misc';
  canonical_filename: string;
  file_path: string;
  file_extension: string;
  file_size_bytes: number | null;
  created_at: string;
  updated_at: string;
}

interface VersionFilesProps {
  versionId: number;
  songId: number;
  versionTitle: string;
  isExpanded?: boolean;
  onToggle?: () => void;
}

const FILE_TYPE_LABELS: Record<string, string> = {
  bulletin: 'Bulletin',
  chart: 'Chart',
  choir: 'Choir',
  instrumental: 'Instrumental',
  misc: 'Misc'
};

const FILE_TYPE_ICONS: Record<string, string> = {
  bulletin: '📄',
  chart: '🎸',
  choir: '🎵',
  instrumental: '🎺',
  misc: '📁'
};

export const VersionFiles: React.FC<VersionFilesProps> = ({
  versionId,
  songId,
  versionTitle,
  isExpanded = false,
  onToggle
}) => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const [files, setFiles] = useState<VersionFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState<string>('bulletin');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchFiles = useCallback(async () => {
    if (!versionId) return;

    setLoading(true);
    setError(null);
    try {
      console.log('[VersionFiles] Fetching files for version:', versionId);
      const response = await apiClient.get(`/versions/${versionId}/files`);
      console.log('[VersionFiles] Files fetched:', response.data);
      setFiles(response.data.data || []);
    } catch (err: any) {
      console.error('[VersionFiles] Error fetching files:', err);
      // Don't show error for 404 - just means no files yet
      if (err.response?.status !== 404) {
        setError(err.response?.data?.message || 'Failed to load files');
      }
    } finally {
      setLoading(false);
    }
  }, [versionId]);

  useEffect(() => {
    if (isExpanded && versionId) {
      fetchFiles();
    }
  }, [versionId, isExpanded, fetchFiles]);

  const handleDeleteFile = (fileId: number, filename: string) => {
    confirm({
      title: 'Delete File',
      message: `Delete "${filename}"? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/version-files/${fileId}`);
          toast.success('File deleted successfully');
          fetchFiles();
        } catch (err: any) {
          console.error('[VersionFiles] Error deleting file:', err);
          toast.error(err.response?.data?.message || 'Failed to delete file');
        }
      }
    });
  };

  // Store pending upload info for retry with overwrite
  const pendingUpload = useRef<{ tempPath: string } | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file extension before uploading
    const validExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!validExtensions.includes(ext)) {
      toast.error(`Invalid file type. Allowed: ${validExtensions.join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    try {
      // Step 1: Upload file to temp location
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await apiClient.post('/file-management/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!uploadResponse.data.success) {
        throw new Error(uploadResponse.data.error || 'Upload failed');
      }

      const tempPath = uploadResponse.data.tempPath;

      // Store temp path in case we need to retry with overwrite
      pendingUpload.current = { tempPath };

      // Step 2: Rename and move file with canonical naming
      const renameResponse = await apiClient.post('/file-management/rename', {
        songId: songId,
        versionId: versionId,
        versionTitle: versionTitle,  // Include version title for unique filename
        category: selectedFileType,  // bulletin, choir, chart, etc.
        originalFilePath: tempPath,
        performanceType: selectedFileType === 'bulletin' ? 'CONG' : selectedFileType.toUpperCase(),
        versionNumber: '1'
      });

      if (renameResponse.data.success) {
        toast.success(`File uploaded: ${renameResponse.data.canonicalFilename}`);
        setShowAddForm(false);
        pendingUpload.current = null;
        fetchFiles();
      } else {
        throw new Error(renameResponse.data.error || 'Rename failed');
      }
    } catch (err: any) {
      console.error('[VersionFiles] Upload error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Upload failed';

      // Check if error is about file already existing
      if (errorMessage.includes('Target file already exists')) {
        // Extract filename from error message
        const match = errorMessage.match(/Target file already exists: (.+?)\./);
        const filename = match ? match[1] : 'this file';

        // Ask user if they want to overwrite
        confirm({
          title: 'File Already Exists',
          message: `A file named "${filename}" already exists on the server. Do you want to replace it?`,
          confirmText: 'Replace',
          variant: 'warning',
          onConfirm: handleFileUploadWithOverwrite
        });
      } else {
        toast.error(errorMessage);
        pendingUpload.current = null;
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileUploadWithOverwrite = async () => {
    if (!pendingUpload.current) {
      toast.error('No pending upload to retry');
      return;
    }

    setUploading(true);
    try {
      const renameResponse = await apiClient.post('/file-management/rename', {
        songId: songId,
        versionId: versionId,
        category: selectedFileType,
        originalFilePath: pendingUpload.current.tempPath,
        performanceType: selectedFileType === 'bulletin' ? 'CONG' : selectedFileType.toUpperCase(),
        versionNumber: '1',
        overwrite: true
      });

      if (renameResponse.data.success) {
        toast.success(`File replaced: ${renameResponse.data.canonicalFilename}`);
        setShowAddForm(false);
        pendingUpload.current = null;
        fetchFiles();
      } else {
        throw new Error(renameResponse.data.error || 'Replace failed');
      }
    } catch (err: any) {
      console.error('[VersionFiles] Overwrite error:', err);
      toast.error(err.response?.data?.error || err.message || 'Replace failed');
    } finally {
      setUploading(false);
      pendingUpload.current = null;
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileUrl = (file: VersionFile): string => {
    // Flat storage: file_path is just the filename
    return `${FILE_SERVER_URL}/files/${file.file_path}`;
  };

  // Collapsed view - just show count
  if (!isExpanded) {
    return (
      <div
        className="version-files version-files--collapsed"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && onToggle?.()}
      >
        <span className="version-files__toggle">
          ▶ {files.length > 0 ? `${files.length} file(s)` : 'No files'}
        </span>
      </div>
    );
  }

  return (
    <div className="version-files version-files--expanded">
      <div
        className="version-files__header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && onToggle?.()}
      >
        <span className="version-files__toggle">
          ▼ Files for "{versionTitle}"
        </span>
      </div>

      <div className="version-files__content">
        {loading && (
          <div className="version-files__loading">Loading files...</div>
        )}

        {error && (
          <div className="version-files__error">{error}</div>
        )}

        {!loading && !error && files.length === 0 && (
          <div className="version-files__empty">
            No files attached to this version.
          </div>
        )}

        {!loading && files.length > 0 && (
          <table className="version-files__table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Filename</th>
                <th>Extension</th>
                <th>Size</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.file_id}>
                  <td>
                    <span className="version-files__type-badge" data-type={file.file_type}>
                      {FILE_TYPE_ICONS[file.file_type]} {FILE_TYPE_LABELS[file.file_type]}
                    </span>
                  </td>
                  <td className="version-files__filename">
                    <span title={file.canonical_filename}>
                      {file.canonical_filename.length > 40
                        ? file.canonical_filename.substring(0, 40) + '...'
                        : file.canonical_filename
                      }
                    </span>
                  </td>
                  <td>{file.file_extension.toUpperCase()}</td>
                  <td>{formatFileSize(file.file_size_bytes)}</td>
                  <td>
                    <div className="version-files__actions">
                      <a
                        href={getFileUrl(file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                        title="View file"
                      >
                        View
                      </a>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeleteFile(file.file_id, file.canonical_filename)}
                        title="Delete file"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Add File Section */}
        <div className="version-files__add-section">
          {!showAddForm ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() => setShowAddForm(true)}
            >
              + Add File
            </button>
          ) : (
            <div className="version-files__add-form">
              <select
                value={selectedFileType}
                onChange={(e) => setSelectedFileType(e.target.value)}
                className="form-control form-control-sm"
                disabled={uploading}
              >
                <option value="bulletin">Bulletin (congregation)</option>
                <option value="choir">Choir</option>
                <option value="chart">Chart</option>
                <option value="instrumental">Instrumental</option>
                <option value="misc">Misc</option>
              </select>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
                onChange={handleFileUpload}
                disabled={uploading}
                className="form-control form-control-sm"
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowAddForm(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              {uploading && <span className="version-files__uploading">Uploading...</span>}
            </div>
          )}
        </div>
      </div>
      <ConfirmModal />
    </div>
  );
};

export default VersionFiles;
